import dayjs from "dayjs";
import React, { createContext, ReactNode, useState } from "react";

import { Type_Filters } from "src/components/web/FilterElements";
import { Type_Category } from "src/types/category";
import { Type_Element, Type_Element_Data } from "src/types/element";
import { Type_Map_Formatted } from "src/types/map";
import { Type_User } from "src/types/user";

const MapContext = createContext<{
    selectedMap: Type_Map_Formatted | null;
    maps: Type_Map_Formatted[];
    updateSelectedMap: (map: Type_Map_Formatted | null) => void;
    updateMaps: (maps: Type_Map_Formatted[]) => void;
    newElement: Type_Element | null;
    updateNewElement: (element: Type_Element | null) => void;
    categories: Type_Category[];
    updateCategories: (data: Type_Category[]) => void;
    elements: Type_Element_Data[];
    updateElements: (data: Type_Element_Data[]) => void;
    filters: Type_Filters;
    updateFilters: (filters: Type_Filters) => void;
    isAuthenticated: boolean;
    updateAuthStatus: (status: boolean) => void;
    connectedUser: Type_User | null;
    updateConnectedUser: (user: Type_User) => void;
}>({
    selectedMap: null,
    maps: [],
    newElement: null,
    updateSelectedMap: () => {},
    updateMaps: () => {},
    updateNewElement: () => {},
    categories: [],
    updateCategories: () => {},
    elements: [],
    updateElements: () => {},
    filters: {
        selectedCategories: [],
        startDate: null,
        endDate: null,
    },
    updateFilters: () => {},
    isAuthenticated: false,
    updateAuthStatus: () => {},
    connectedUser: null,
    updateConnectedUser: () => {},
});

type MapContextProviderProps = {
    children: ReactNode;
};

const MapContextProvider = ({ children }: MapContextProviderProps) => {
    const [maps, setMaps] = useState<Type_Map_Formatted[]>([]);
    const [selectedMap, setSelectedMap] = useState<Type_Map_Formatted | null>(
        null,
    );
    const [newElement, setNewElement] = useState<Type_Element | null>(null);
    const [categories, setCategories] = useState<Type_Category[]>([]);
    const [elements, setElements] = useState<Type_Element_Data[]>([]);
    const [filters, setFilters] = useState<Type_Filters>({
        selectedCategories: [],
        startDate: dayjs(),
        endDate: null,
    });
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [connectedUser, setConnectedUser] = useState<Type_User | null>(null);

    const updateSelectedMap = (map: Type_Map_Formatted | null) => {
        setSelectedMap(map);
    };

    const updateMaps = (maps: Type_Map_Formatted[]) => {
        setMaps(maps);
    };

    const updateNewElement = (data: Type_Element | null) => {
        setNewElement(data);
    };

    const updateCategories = (data: Type_Category[]) => {
        setCategories(data);
    };

    const updateElements = (data: Type_Element_Data[]) => {
        setElements(data);
    };

    const updateFilters = (data: Type_Filters) => {
        setFilters(data);
    };

    const updateAuthStatus = (status: boolean) => {
        setIsAuthenticated(status);
    };

    const updateConnectedUser = (user: Type_User | null) => {
        setConnectedUser(user);
    };

    return (
        <MapContext.Provider
            value={{
                maps,
                updateMaps,
                selectedMap,
                updateSelectedMap,
                newElement,
                updateNewElement,
                categories,
                updateCategories,
                elements,
                updateElements,
                filters,
                updateFilters,
                isAuthenticated,
                updateAuthStatus,
                connectedUser,
                updateConnectedUser,
            }}
        >
            {children}
        </MapContext.Provider>
    );
};

export { MapContext, MapContextProvider };
